import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from './ui/dialog';
import { toast } from 'sonner';
import {
  Calendar, Clock, Users, ArrowRightLeft, Check, X, AlertTriangle, History
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('pt-BR', { 
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' 
  });
};

// =============================================================================
// DEADLINE EXTENSION COMPONENT
// =============================================================================

export const DeadlineExtensionSection = ({ actId, currentDeadline, onExtensionApproved }) => {
  const [extensions, setExtensions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showRequestDialog, setShowRequestDialog] = useState(false);
  const [newRequest, setNewRequest] = useState({ nova_data: '', justificativa: '' });
  const [submitting, setSubmitting] = useState(false);

  const token = localStorage.getItem('token');
  const api = axios.create({
    baseURL: `${API_URL}/api`,
    headers: { Authorization: `Bearer ${token}` }
  });

  const loadExtensions = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/acts/${actId}/deadline-extensions`);
      setExtensions(res.data);
    } catch (error) {
      console.error('Error loading extensions:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadExtensions();
  }, [actId]);

  const handleRequestExtension = async () => {
    if (!newRequest.nova_data || !newRequest.justificativa) {
      toast.error('Preencha todos os campos');
      return;
    }

    setSubmitting(true);
    try {
      await api.post(`/acts/${actId}/deadline-extensions`, {
        nova_data: new Date(newRequest.nova_data).toISOString(),
        justificativa: newRequest.justificativa
      });
      toast.success('Solicitação de prorrogação enviada');
      setShowRequestDialog(false);
      setNewRequest({ nova_data: '', justificativa: '' });
      loadExtensions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao solicitar prorrogação');
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async (extensionId) => {
    try {
      const res = await api.put(`/deadline-extensions/${extensionId}/approve`);
      toast.success('Prorrogação aprovada');
      loadExtensions();
      if (onExtensionApproved) onExtensionApproved(res.data.nova_data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao aprovar');
    }
  };

  const handleReject = async (extensionId) => {
    try {
      await api.put(`/deadline-extensions/${extensionId}/reject`);
      toast.success('Solicitação rejeitada');
      loadExtensions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao rejeitar');
    }
  };

  const pendingExtension = extensions.find(e => e.status === 'PENDENTE');

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-600" />
            <CardTitle className="text-base">Prorrogação de Prazo</CardTitle>
          </div>
          <Button 
            size="sm" 
            variant="outline" 
            onClick={() => setShowRequestDialog(true)}
            disabled={!!pendingExtension}
          >
            <Clock className="w-4 h-4 mr-2" />
            Solicitar Prorrogação
          </Button>
        </div>
        <CardDescription>
          Prazo atual: <strong>{formatDate(currentDeadline)}</strong>
        </CardDescription>
      </CardHeader>
      <CardContent>
        {pendingExtension && (
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg mb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-amber-800">Solicitação Pendente</p>
                <p className="text-sm text-amber-600">Nova data: {formatDate(pendingExtension.nova_data)}</p>
                <p className="text-sm text-amber-600 mt-1">{pendingExtension.justificativa}</p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="text-green-600" onClick={() => handleApprove(pendingExtension.extension_id)}>
                  <Check className="w-4 h-4 mr-1" /> Aprovar
                </Button>
                <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleReject(pendingExtension.extension_id)}>
                  <X className="w-4 h-4 mr-1" /> Rejeitar
                </Button>
              </div>
            </div>
          </div>
        )}

        {extensions.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm font-medium text-slate-600 flex items-center gap-2">
              <History className="w-4 h-4" /> Histórico de Prorrogações
            </p>
            {extensions.filter(e => e.status !== 'PENDENTE').map(ext => (
              <div key={ext.extension_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <div className="flex items-center gap-2">
                    <Badge className={ext.status === 'APROVADO' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                      {ext.status}
                    </Badge>
                    <span className="text-sm text-slate-600">{formatDate(ext.data_original)} → {formatDate(ext.nova_data)}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">{ext.justificativa}</p>
                </div>
                <span className="text-xs text-slate-400">{formatDate(ext.created_at)}</span>
              </div>
            ))}
          </div>
        )}

        {extensions.length === 0 && !loading && (
          <p className="text-sm text-slate-500 text-center py-4">Nenhuma prorrogação solicitada</p>
        )}

        {/* Dialog Solicitar Prorrogação */}
        <Dialog open={showRequestDialog} onOpenChange={setShowRequestDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Solicitar Prorrogação de Prazo</DialogTitle>
              <DialogDescription>Informe a nova data e justificativa para a prorrogação</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Nova Data *</Label>
                <Input
                  type="datetime-local"
                  value={newRequest.nova_data}
                  onChange={e => setNewRequest(prev => ({ ...prev, nova_data: e.target.value }))}
                />
              </div>
              <div>
                <Label>Justificativa *</Label>
                <Textarea
                  value={newRequest.justificativa}
                  onChange={e => setNewRequest(prev => ({ ...prev, justificativa: e.target.value }))}
                  placeholder="Descreva o motivo da prorrogação..."
                  rows={4}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRequestDialog(false)}>Cancelar</Button>
              <Button onClick={handleRequestExtension} disabled={submitting}>
                {submitting ? 'Enviando...' : 'Solicitar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

// =============================================================================
// TASK DELEGATION COMPONENT
// =============================================================================

export const TaskDelegationSection = ({ actId, entityType = 'control_act', entityId, currentResponsible, onDelegationComplete }) => {
  const [delegations, setDelegations] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDelegateDialog, setShowDelegateDialog] = useState(false);
  const [newDelegation, setNewDelegation] = useState({
    novo_responsavel_id: '',
    motivo: '',
    manter_visibilidade: true
  });
  const [submitting, setSubmitting] = useState(false);

  const token = localStorage.getItem('token');
  const api = axios.create({
    baseURL: `${API_URL}/api`,
    headers: { Authorization: `Bearer ${token}` }
  });

  const loadData = async () => {
    setLoading(true);
    try {
      const [delRes, usersRes] = await Promise.all([
        api.get('/delegations'),
        api.get('/users')
      ]);
      // Filtrar delegações para esta entidade
      setDelegations(delRes.data.filter(d => d.entity_id === (entityId || actId) && d.ativo));
      setUsers(usersRes.data);
    } catch (error) {
      console.error('Error loading delegation data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [actId, entityId]);

  const handleDelegate = async () => {
    if (!newDelegation.novo_responsavel_id || !newDelegation.motivo) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    setSubmitting(true);
    try {
      const endpoint = entityType === 'internal_task' 
        ? `/internal-tasks/${entityId}/delegate`
        : `/acts/${actId}/delegate`;
      
      await api.post(endpoint, newDelegation);
      toast.success('Delegação realizada com sucesso');
      setShowDelegateDialog(false);
      setNewDelegation({ novo_responsavel_id: '', motivo: '', manter_visibilidade: true });
      loadData();
      if (onDelegationComplete) onDelegationComplete();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao delegar');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevoke = async (delegationId) => {
    try {
      await api.delete(`/delegations/${delegationId}`);
      toast.success('Delegação revogada');
      loadData();
      if (onDelegationComplete) onDelegationComplete();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao revogar delegação');
    }
  };

  const activeDelegation = delegations.find(d => d.ativo);

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ArrowRightLeft className="w-5 h-5 text-blue-600" />
            <CardTitle className="text-base">Delegação de Responsabilidade</CardTitle>
          </div>
          <Button 
            size="sm" 
            variant="outline" 
            onClick={() => setShowDelegateDialog(true)}
          >
            <Users className="w-4 h-4 mr-2" />
            Delegar
          </Button>
        </div>
        {currentResponsible && (
          <CardDescription>
            Responsável atual: <strong>{currentResponsible}</strong>
          </CardDescription>
        )}
      </CardHeader>
      <CardContent>
        {activeDelegation && (
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg mb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-blue-800">Delegação Ativa</p>
                <p className="text-sm text-blue-600">
                  {activeDelegation.responsavel_original_email} → {activeDelegation.novo_responsavel_email}
                </p>
                <p className="text-sm text-blue-600 mt-1">{activeDelegation.motivo}</p>
                <p className="text-xs text-blue-400 mt-1">Delegado em: {formatDate(activeDelegation.delegado_em)}</p>
              </div>
              <Button 
                size="sm" 
                variant="outline" 
                className="text-red-600"
                onClick={() => handleRevoke(activeDelegation.delegation_id)}
              >
                <X className="w-4 h-4 mr-1" /> Revogar
              </Button>
            </div>
          </div>
        )}

        {!activeDelegation && !loading && (
          <p className="text-sm text-slate-500 text-center py-4">Nenhuma delegação ativa</p>
        )}

        {/* Dialog Delegar */}
        <Dialog open={showDelegateDialog} onOpenChange={setShowDelegateDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delegar Responsabilidade</DialogTitle>
              <DialogDescription>Transfira a responsabilidade para outro usuário</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Novo Responsável *</Label>
                <Select 
                  value={newDelegation.novo_responsavel_id} 
                  onValueChange={v => setNewDelegation(prev => ({ ...prev, novo_responsavel_id: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o usuário" />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map(u => (
                      <SelectItem key={u.user_id} value={u.user_id}>
                        {u.name || u.email} ({u.role})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Motivo da Delegação *</Label>
                <Textarea
                  value={newDelegation.motivo}
                  onChange={e => setNewDelegation(prev => ({ ...prev, motivo: e.target.value }))}
                  placeholder="Descreva o motivo da delegação..."
                  rows={3}
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="manter_visibilidade"
                  checked={newDelegation.manter_visibilidade}
                  onChange={e => setNewDelegation(prev => ({ ...prev, manter_visibilidade: e.target.checked }))}
                  className="rounded"
                />
                <Label htmlFor="manter_visibilidade" className="text-sm font-normal">
                  Manter visibilidade para o responsável original
                </Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDelegateDialog(false)}>Cancelar</Button>
              <Button onClick={handleDelegate} disabled={submitting} className="bg-blue-600 hover:bg-blue-700">
                {submitting ? 'Delegando...' : 'Confirmar Delegação'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

// =============================================================================
// COMBINED MANAGEMENT PANEL
// =============================================================================

export const ActManagementPanel = ({ actId, act, onUpdate }) => {
  if (!act) return null;

  return (
    <div className="space-y-4">
      <DeadlineExtensionSection 
        actId={actId} 
        currentDeadline={act.deadline}
        onExtensionApproved={(newDate) => {
          if (onUpdate) onUpdate({ ...act, deadline: newDate });
        }}
      />
      <TaskDelegationSection 
        actId={actId}
        entityType="control_act"
        entityId={actId}
        currentResponsible={act.responsible_unit || act.created_by}
        onDelegationComplete={() => {
          if (onUpdate) onUpdate();
        }}
      />
    </div>
  );
};

export default ActManagementPanel;
